import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Col,
  DatePicker,
  Divider,
  Form,
  Input,
  message,
  Modal,
  Row,
  Select,
  Space,
  Statistic,
  Tabs,
  Tag,
} from "antd";
import { useSelector } from "react-redux";
import {
  useGetAnalyticsDatasetsQuery,
  useGetAnalyticsDatasetFieldsQuery,
  useGetAnalyticsDatasetSubmittersQuery,
  useRunAnalyticsQueryMutation,
  useExportAnalyticsReportMutation,
  useCreateSavedReportMutation,
} from "../../../Redux/service/Analytics/Analytics";
import { useGetAllUsersQuery } from "../../../Redux/service/Users/AuthUsers";
import chartRegistry from "../Visualizations/chartRegistry";
import trans from "../../../utils";
import { DownloadOutlined, FilterOutlined, PlusOutlined, SettingOutlined } from "@ant-design/icons";

const { Option } = Select;

const localizedMetricTypes = () => [
  { value: "count", label: trans("Count") },
  { value: "distinctCount", label: trans("Distinct Count") },
  { value: "sum", label: trans("Sum") },
  { value: "average", label: trans("Average") },
  { value: "min", label: trans("Minimum") },
  { value: "max", label: trans("Maximum") },
  { value: "timeline", label: trans("Timeline") },
  { value: "percentChange", label: trans("Percent Change") },
  { value: "consumptionRate", label: trans("Consumption Rate") },
  { value: "formula", label: trans("Formula") },
];

const localizedOps = () => [
  { value: "eq", label: "=" },
  { value: "neq", label: "!=" },
  { value: "gt", label: ">" },
  { value: "gte", label: ">=" },
  { value: "lt", label: "<" },
  { value: "lte", label: "<=" },
  { value: "in", label: trans("In") },
  { value: "contains", label: trans("Contains") },
];

export default function ReportBuilder() {
  const { data: datasetsData } = useGetAnalyticsDatasetsQuery();
  const datasets = datasetsData?.data || [];

  const [form] = Form.useForm();
  const [datasetId, setDatasetId] = useState(null);
  const [dimensions, setDimensions] = useState([]);
  const [metrics, setMetrics] = useState([{ type: "count" }]);
  const [cards, setCards] = useState([]);
  const [filters, setFilters] = useState([]);
  const [dateRange, setDateRange] = useState([null, null]);
  const [activeChart, setActiveChart] = useState("table");
  const [reportName, setReportName] = useState("");
  const [assignedToUserIds, setAssignedToUserIds] = useState([]);
  const [isSaveModalOpen, setIsSaveModalOpen] = useState(false);
  const user = useSelector((state) => state.users.user);
  const { data: allUsersData, isLoading: usersLoading } = useGetAllUsersQuery({ page: 1, limit: 10000 }, { skip: !user?.administrator });
  const allUsers = allUsersData?.data || [];
  const [runQuery, { data: resultData, isLoading, error: runError }] = useRunAnalyticsQueryMutation();
  const [exportReport, { isLoading: isExporting }] = useExportAnalyticsReportMutation();
  const [exportFormat, setExportFormat] = useState(null);
  const [createSavedReport, { isLoading: isSaving }] = useCreateSavedReportMutation();
  const [activeFormula, setActiveFormula] = useState(null);
  const [helperField, setHelperField] = useState(null);
  const [helperFn, setHelperFn] = useState("first");
  const formulaRefs = useRef({});

  const result = resultData?.data || { columns: [], rows: [], cards: [] };

  function applyToActiveFormula(insertText) {
    if (!activeFormula) {
      message.warning(trans("Click a formula field first to focus it"));
      return;
    }
    const { kind, index } = activeFormula;
    const refKey = `${kind}-${index}`;
    const input = formulaRefs.current[refKey]?.resizableTextArea?.textArea || formulaRefs.current[refKey];
    const current = kind === "metric" ? metrics[index].formula || "" : cards[index].formula || "";
    const start = input?.selectionStart ?? current.length;
    const end = input?.selectionEnd ?? start;
    const next = current.slice(0, start) + insertText + current.slice(end);
    const updater = kind === "metric" ? updateMetric : updateCard;
    updater(index, "formula", next);
    if (input) {
      setTimeout(() => {
        input.focus();
        input.selectionStart = input.selectionEnd = start + insertText.length;
      }, 0);
    }
  }

  const { data: fieldsData } = useGetAnalyticsDatasetFieldsQuery(datasetId, { skip: !datasetId });
  const fields = fieldsData?.data || [];

  const { data: submittersData } = useGetAnalyticsDatasetSubmittersQuery(datasetId, {
    skip: !datasetId,
  });
  const submitters = submittersData?.data || [];

  const fieldOptions = useMemo(
    () =>
      fields.map((f) => ({
        value: f.fieldId,
        label: `${f.title || f.key} (${f.valueType})`,
        field: f,
      })),
    [fields]
  );

  useEffect(() => {
    if (datasetId) {
      setDimensions([]);
      setMetrics([{ type: "count" }]);
      setCards([]);
      setFilters([]);
    }
  }, [datasetId]);

  function buildQuery() {
    if (!datasetId) return null;
    return {
      datasetId,
      dimensions: dimensions.map((d) => ({ fieldId: d })),
      metrics: metrics.map((m) => ({ type: m.type, fieldId: m.fieldId || undefined, alias: m.alias, formula: m.formula })),
      cards: cards.map((c) => ({ type: c.type, fieldId: c.fieldId || undefined, alias: c.alias, formula: c.formula })),
      filters: filters.map((f) => ({ fieldId: f.fieldId, op: f.op, value: f.value })),
      dateRange:
        dateRange[0] && dateRange[1]
          ? { from: dateRange[0].toISOString(), to: dateRange[1].toISOString() }
          : undefined,
    };
  }

  function handleRun() {
    const query = buildQuery();
    if (!query) return;
    runQuery(query);
  }

  async function handleExport(format) {
    const query = buildQuery();
    if (!query) return;

    setExportFormat(format);
    const dataset = datasets.find((d) => d.id === datasetId);
    const title = reportName.trim() || dataset?.name || "Analytics_Report";

    try {
      const blob = await exportReport({ query, format, title }).unwrap();
      const extension = format === "pdf" ? "pdf" : "xlsx";
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${title.replace(/\s+/g, "_")}_${new Date().toISOString().split("T")[0]}.${extension}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      message.error(err?.message || trans("Failed to export"));
    } finally {
      setExportFormat(null);
    }
  }

  async function handleSave() {
    const query = buildQuery();
    if (!query) return;
    if (!reportName.trim()) {
      message.warning(trans("Please enter a report name"));
      return;
    }
    try {
      const payload = {
        name: reportName.trim(),
        query,
        assignedToUserIds: user?.administrator ? assignedToUserIds : undefined,
      };
      await createSavedReport(payload).unwrap();
      message.success(trans("Report saved"));
      setReportName("");
      setAssignedToUserIds([]);
      setIsSaveModalOpen(false);
    } catch (err) {
      message.error(err?.data?.message || trans("Failed to save report"));
    }
  }

  function updateMetric(index, key, value) {
    const next = [...metrics];
    next[index] = { ...next[index], [key]: value };
    setMetrics(next);
  }

  function removeMetric(index) {
    setMetrics((prev) => prev.filter((_, i) => i !== index));
  }

  function addMetric() {
    setMetrics((prev) => [...prev, { type: "count" }]);
  }

  function updateCard(index, key, value) {
    const next = [...cards];
    next[index] = { ...next[index], [key]: value };
    setCards(next);
  }

  function removeCard(index) {
    setCards((prev) => prev.filter((_, i) => i !== index));
  }

  function addCard() {
    setCards((prev) => [...prev, { type: "count" }]);
  }

  function updateFilter(index, key, value) {
    setFilters((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [key]: value };
      return next;
    });
  }

  function removeFilter(index) {
    setFilters((prev) => prev.filter((_, i) => i !== index));
  }

  function addFilter() {
    setFilters((prev) => [...prev, { fieldId: null, op: "eq", value: "" }]);
  }

  const ChartComponent = chartRegistry[activeChart]?.component || chartRegistry.table.component;

  return (
    <Card title={trans("Report Builder")} style={{ margin: 16 }}>
      <Form layout="vertical" form={form}>
        <Row gutter={16}>
          <Col span={8}>
            <Form.Item label={trans("Dataset")}>
              <Select
                placeholder={trans("Select a form/dataset")}
                showSearch
                optionFilterProp="children"
                onChange={(val) => setDatasetId(val)}
                value={datasetId}
              >
                {datasets.map((ds) => (
                  <Option key={ds.id} value={ds.id}>
                    {ds.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item label={trans("Date Range")}>
              <DatePicker.RangePicker
                style={{ width: "100%" }}
                onChange={setDateRange}
                value={dateRange}
              />
            </Form.Item>
          </Col>
        </Row>

        {fields.length > 0 && (
          <Card size="small" title={trans("Available Fields")} style={{ marginBottom: 16 }}>
            <Space wrap>
              {fields.map((f) => (
                <Tag key={f.fieldId} color="blue" title={f.fieldId} dir="auto">
                  {f.title || f.key} <span style={{ opacity: 0.7 }}>({f.valueType})</span>
                </Tag>
              ))}
            </Space>
            <div style={{ marginTop: 8, color: "#666", fontSize: 12 }}>
              {trans("Use the field title in formulas")}
            </div>
          </Card>
        )}

        {activeFormula && (
          <Card size="small" title={trans("Formula Helper")} style={{ marginBottom: 16 }}>
            <Space wrap align="center">
              <Select
                placeholder={trans("Function")}
                style={{ width: 130 }}
                value={helperFn}
                options={[
                  { value: "first", label: "first" },
                  { value: "last", label: "last" },
                  { value: "sum", label: "sum" },
                  { value: "avg", label: "avg" },
                  { value: "min", label: "min" },
                  { value: "max", label: "max" },
                  { value: "count", label: "count" },
                ]}
                onChange={setHelperFn}
              />
              <Select
                placeholder={trans("Field")}
                style={{ width: 200 }}
                value={helperField}
                options={fieldOptions}
                onChange={setHelperField}
                disabled={!datasetId || helperFn === "count"}
              />
              <Button
                type="primary"
                size="small"
                onClick={() => {
                  if (!activeFormula) {
                    message.warning(trans("Click a formula field first to focus it"));
                    return;
                  }
                  if (helperFn === "count") {
                    applyToActiveFormula("count()");
                  } else if (helperField) {
                    const field = fieldOptions.find((o) => o.value === helperField)?.field;
                    const title = field?.title || helperField;
                    applyToActiveFormula(`${helperFn}("${title.replace(/"/g, '\\"')}")`);
                  }
                }}
              >
                {trans("Insert Function")}
              </Button>
              {["+", "-", "*", "/", "%", "(", ")"].map((op) => (
                <Button key={op} size="small" onClick={() => applyToActiveFormula(` ${op} `)}>
                  {op}
                </Button>
              ))}
            </Space>
            <div style={{ marginTop: 8, color: "#666", fontSize: 12 }}>
              {trans("Click inside a formula field")}
            </div>
          </Card>
        )}

        <Divider orientation="left">{trans("Dimensions")}</Divider>
        <Select
          mode="multiple"
          placeholder={trans("Select dimensions")}
          style={{ width: "100%", marginBottom: 16 }}
          options={fieldOptions}
          onChange={setDimensions}
          value={dimensions}
          disabled={!datasetId}
        />

        <Divider orientation="left">{trans("Metrics")}</Divider>
        <Space direction="vertical" style={{ width: "100%", marginBottom: 16 }}>
          {metrics.map((metric, idx) => (
            <Row key={idx} gutter={8} align="middle">
              <Col span={6}>
                <Select
                  value={metric.type}
                  style={{ width: "100%" }}
                  options={localizedMetricTypes()}
                  onChange={(val) => updateMetric(idx, "type", val)}
                />
              </Col>
              <Col span={metric.type === "formula" ? 14 : 8}>
                {metric.type === "formula" ? (
                  <Input.TextArea
                    ref={(el) => {
                      formulaRefs.current[`metric-${idx}`] = el;
                    }}
                    rows={2}
                    dir="auto"
                    placeholder='e.g. (first("Temperature") - last("Temperature")) / first("Temperature") * 100'
                    value={metric.formula || ""}
                    onChange={(e) => updateMetric(idx, "formula", e.target.value)}
                    onFocus={() => setActiveFormula({ kind: "metric", index: idx })}
                  />
                ) : (
                  <Select
                    allowClear
                    placeholder={trans("Field (optional for count)")}
                    style={{ width: "100%" }}
                    options={fieldOptions}
                    value={metric.fieldId}
                    onChange={(val) => updateMetric(idx, "fieldId", val)}
                    disabled={metric.type === "count"}
                  />
                )}
              </Col>
              <Col span={metric.type === "formula" ? 4 : 6}>
                <Input
                  placeholder={trans("Alias")}
                  value={metric.alias || ""}
                  onChange={(e) => updateMetric(idx, "alias", e.target.value)}
                />
              </Col>
              <Col span={4}>
                {metrics.length > 1 && (
                  <Button danger onClick={() => removeMetric(idx)}>
                    {trans("Remove")}
                  </Button>
                )}
              </Col>
            </Row>
          ))}
          <Button icon={<PlusOutlined />} onClick={addMetric}>
            {trans("Add Metric")}
          </Button>
        </Space>

        <Divider orientation="left">{trans("KPI Cards")}</Divider>
        <Space direction="vertical" style={{ width: "100%", marginBottom: 16 }}>
          {cards.map((card, idx) => (
            <Row key={idx} gutter={8} align="middle">
              <Col span={6}>
                <Select
                  value={card.type}
                  style={{ width: "100%" }}
                  options={localizedMetricTypes()}
                  onChange={(val) => updateCard(idx, "type", val)}
                />
              </Col>
              <Col span={card.type === "formula" ? 14 : 8}>
                {card.type === "formula" ? (
                  <Input.TextArea
                    ref={(el) => {
                      formulaRefs.current[`card-${idx}`] = el;
                    }}
                    rows={2}
                    dir="auto"
                    placeholder='e.g. (first("Temperature") - last("Temperature")) / first("Temperature") * 100'
                    value={card.formula || ""}
                    onChange={(e) => updateCard(idx, "formula", e.target.value)}
                    onFocus={() => setActiveFormula({ kind: "card", index: idx })}
                  />
                ) : (
                  <Select
                    allowClear
                    placeholder={trans("Field")}
                    style={{ width: "100%" }}
                    options={fieldOptions}
                    value={card.fieldId}
                    onChange={(val) => updateCard(idx, "fieldId", val)}
                    disabled={card.type === "count"}
                  />
                )}
              </Col>
              <Col span={card.type === "formula" ? 4 : 6}>
                <Input
                  placeholder={trans("Alias")}
                  value={card.alias || ""}
                  onChange={(e) => updateCard(idx, "alias", e.target.value)}
                />
              </Col>
              <Col span={4}>
                <Button danger onClick={() => removeCard(idx)}>
                  {trans("Remove")}
                </Button>
              </Col>
            </Row>
          ))}
          <Button icon={<PlusOutlined />} onClick={addCard}>
            {trans("Add Card")}
          </Button>
        </Space>

        <Divider orientation="left">
          <FilterOutlined /> {trans("Filters")}
        </Divider>
        <Space direction="vertical" style={{ width: "100%", marginBottom: 16 }}>
          {filters.map((filter, idx) => {
            const isSubmittedBy = filter.fieldId === "__submittedBy__";
            return (
              <Row key={idx} gutter={8} align="middle">
                <Col span={8}>
                  <Select
                    placeholder={trans("Field")}
                    style={{ width: "100%" }}
                    options={fieldOptions}
                    value={filter.fieldId}
                    onChange={(val) => {
                      if (val === "__submittedBy__") {
                        updateFilter(idx, "fieldId", val);
                        updateFilter(idx, "op", "in");
                        updateFilter(idx, "value", []);
                      } else {
                        updateFilter(idx, "fieldId", val);
                        updateFilter(idx, "value", "");
                      }
                    }}
                  />
                </Col>
                <Col span={4}>
                  <Select
                    style={{ width: "100%" }}
                    options={localizedOps()}
                    value={filter.op}
                    onChange={(val) => updateFilter(idx, "op", val)}
                  />
                </Col>
                <Col span={8}>
                  {isSubmittedBy ? (
                    <Select
                      mode="multiple"
                      allowClear
                      placeholder={trans("Select submitters")}
                      style={{ width: "100%" }}
                      options={submitters.map((u) => ({ value: u.name, label: u.name }))}
                      value={Array.isArray(filter.value) ? filter.value : []}
                      onChange={(val) => updateFilter(idx, "value", val)}
                    />
                  ) : (
                    <Input
                      placeholder={trans("Value")}
                      value={filter.value}
                      onChange={(e) => updateFilter(idx, "value", e.target.value)}
                    />
                  )}
                </Col>
                <Col span={4}>
                  <Button danger onClick={() => removeFilter(idx)}>
                    {trans("Remove")}
                  </Button>
                </Col>
              </Row>
            );
          })}
          <Button icon={<PlusOutlined />} onClick={addFilter}>
            {trans("Add Filter")}
          </Button>
        </Space>

        <Space>
          <Button type="primary" size="large" onClick={handleRun} loading={isLoading} disabled={!datasetId}>
            {trans("Generate Report")}
          </Button>
          <Button
            size="large"
            icon={<DownloadOutlined />}
            onClick={() => handleExport("excel")}
            loading={isExporting && exportFormat === "excel"}
            disabled={!datasetId || isExporting}
          >
            {trans("Excel")}
          </Button>
          <Button
            size="large"
            icon={<DownloadOutlined />}
            onClick={() => handleExport("pdf")}
            loading={isExporting && exportFormat === "pdf"}
            disabled={!datasetId || isExporting}
          >
            {trans("PDF")}
          </Button>
          <Button size="large" onClick={() => setIsSaveModalOpen(true)} disabled={!datasetId}>
            {trans("Save Report")}
          </Button>
        </Space>
      </Form>

      <Modal
        title={trans("Save Report")}
        open={isSaveModalOpen}
        onCancel={() => {
          setIsSaveModalOpen(false);
          setReportName("");
          setAssignedToUserIds([]);
        }}
        onOk={handleSave}
        confirmLoading={isSaving}
        okButtonProps={{ disabled: !reportName.trim() }}
        okText={trans("Save")}
        cancelText={trans("Cancel")}
      >
        <Form layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item label={trans("Report name")} required>
            <Input
              placeholder={trans("Enter report name")}
              value={reportName}
              onChange={(e) => setReportName(e.target.value)}
              autoFocus
            />
          </Form.Item>
          {user?.administrator && (
            <Form.Item label={trans("Assign to users")}>
              <Select
                mode="multiple"
                allowClear
                showSearch
                loading={usersLoading}
                placeholder={trans("Select users")}
                optionFilterProp="children"
                value={assignedToUserIds}
                onChange={setAssignedToUserIds}
              >
                {allUsers.map((u) => (
                  <Option key={u.id} value={u.id}>
                    {u.name} ({u.email})
                  </Option>
                ))}
              </Select>
            </Form.Item>
          )}
        </Form>
      </Modal>

      {runError && (
        <Alert
          message={runError?.data?.message || runError?.message || trans("Failed to run query")}
          type="error"
          showIcon
          style={{ marginTop: 16 }}
        />
      )}

      {result.cards?.length > 0 && (
        <>
          <Divider orientation="left">{trans("Dashboard Cards")}</Divider>
          <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
            {result.cards.map((card) => (
              <Col key={card.key} xs={24} sm={12} md={8} lg={6}>
                <Card>
                  <Statistic title={card.label} value={card.value ?? "-"} />
                </Card>
              </Col>
            ))}
          </Row>
        </>
      )}

      {result.columns.length > 0 && (
        <>
          <Divider />
          <Tabs
            activeKey={activeChart}
            onChange={setActiveChart}
            tabBarExtraContent={<SettingOutlined />}
            items={Object.entries(chartRegistry).map(([key, { label }]) => ({
              key,
              label: trans(label),
              children: <ChartComponent columns={result.columns} rows={result.rows} />,
            }))}
          />
          <Row style={{ marginTop: 8 }}>
            <Col>
              {result.columns.map((col) => (
                <Tag key={col.key}>{col.label}</Tag>
              ))}
            </Col>
          </Row>
        </>
      )}
    </Card>
  );
}
